import { beginCell } from '@ton/core'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { useCallback } from 'react'

import { donationApi, getCreateItemOrderApi, getHintInvoiceApi, getOrderApi } from '@api'
import { webApp } from '@shared'
import {
  useAppActions,
  useInventoryActions,
  useOverlayActions,
  useUserActions,
} from '@store'

export const usePaymentOrder = () => {
  const { setOverlayQueue } = useOverlayActions()
  const { fetchProfile } = useUserActions()
  const { getItems } = useInventoryActions()
  const [tonConnectUI] = useTonConnectUI()
  const wallet = useTonWallet()
  const { followRefresh } = useAppActions()

  const updateData = useCallback(async () => {
    fetchProfile()
    getItems()
  }, [])

  const MAX_POLL_ATTEMPTS = 24
  const POLL_INTERVAL_MS = 5000

  const pollOrderStatus = useCallback(
    (orderId: string) => {
      let cancelled = false
      let attempts = 0
      let timeoutId: ReturnType<typeof setTimeout> | null = null

      const stop = () => {
        if (timeoutId) clearTimeout(timeoutId)
      }

      const tick = async () => {
        if (cancelled) return
        attempts++

        const { ok, data } = await getOrderApi(orderId)
        if (!ok || !data) {
          if (attempts >= MAX_POLL_ATTEMPTS) {
            stop()
            return
          }
          timeoutId = setTimeout(tick, POLL_INTERVAL_MS)
          return
        }

        const status = data.status
        if (status === 'processed') {
          stop()
          await updateData()
          return
        }

        if (status === 'cancel' || status === 'cancelled') {
          stop()
          return
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          stop()
          return
        }

        timeoutId = setTimeout(tick, POLL_INTERVAL_MS)
      }

      void tick()

      return () => {
        cancelled = true
        if (timeoutId) clearTimeout(timeoutId)
      }
    },
    [updateData]
  )

  const purchaseForXTR = useCallback(
    async (itemId: string, quantity?: number) => {
      followRefresh()
      const { ok, data } = await getCreateItemOrderApi(itemId, 'XTR', quantity)
      if (ok && data) {
        webApp.openInvoice(
          data.invoice_url.invoice_url,
          async (status: string) => {
            if (status === 'paid') {
              updateData()
            }
          }
        )
      }
    },
    [updateData]
  )

  const purchaseHintForXTR = useCallback(
    async (hintId: number, onPaid: () => void | Promise<void>) => {
      const { ok, data } = await getHintInvoiceApi(hintId)
      if (ok && data) {
        webApp.openInvoice(data.invoice_url, async (status: string) => {
          if (status === 'paid') {
            await onPaid()
          }
        })
      }
    },
    []
  )

  const donateForXTR = useCallback(async (amount: number)=> {
    followRefresh()
    const { ok, data } = await donationApi(amount, 'XTR')
    if (ok && data) {
      webApp.openInvoice(
        data.invoice_url.invoice_url,
        async (status: string) => {
          if (status === 'paid') {
            console.log("Stars donatet success")
          }
        }
      )
    }
  },[])

  const donateForTON = useCallback(async (amount: number) => {
    followRefresh()
    if (!wallet) {
      tonConnectUI.openModal()
      return
    }
    let orderID: string | null = null
    const { ok, data } = await donationApi(amount, 'TON')

    if (!ok || !data) {
      throw new Error('Failed to create order')
    }
    orderID = data.order.order_id
    const payload = beginCell()
      .storeUint(0, 32)
      .storeStringTail(orderID)
      .endCell()
      .toBoc()
      .toString('base64')

    const transaction = {
      validUntil: Math.floor(
        new Date(data.order.expires_at).getTime() / 1000
      ),
      messages: [
        {
          address: data.order.address,
          amount: String(data?.order.amount),
          payload: payload,
        },
      ],
    }

    await tonConnectUI
    .sendTransaction(transaction)
    .then(() => {
      setOverlayQueue([
        {
          type: 'transactionProcessing',
        },
      ])
      pollOrderStatus(orderID)
    })
    .catch(() => {
      // Transaction failed
    })
  },[])

  const purchaseForTON = useCallback(
    async (itemId: string, quantity?: number) => {
      followRefresh()
      if (!wallet) {
        tonConnectUI.openModal()
        return
      }

      let orderID: string | null = null

      const { ok, data } = await getCreateItemOrderApi(itemId, 'TON', quantity)

      if (!ok || !data) {
        throw new Error('Failed to create order')
      }

      orderID = data.order.order_id

      const payload = beginCell()
        .storeUint(0, 32)
        .storeStringTail(orderID)
        .endCell()
        .toBoc()
        .toString('base64')

      const transaction = {
        validUntil: Math.floor(
          new Date(data.order.expires_at).getTime() / 1000
        ),
        messages: [
          {
            address: data.order.address,
            amount: String(data?.order.amount),
            payload: payload,
          },
        ],
      }

      await tonConnectUI
        .sendTransaction(transaction)
        .then(() => {
          setOverlayQueue([
            {
              type: 'transactionProcessing',
            },
          ])
          pollOrderStatus(orderID)
        })
        .catch(() => {
          // Transaction failed
        })
    },
    [wallet, tonConnectUI, pollOrderStatus]
  )

  // const payHintForTON = useCallback(
  //   async (onPaid?: () => void | Promise<void>) => {
  //     followRefresh()
  //     if (!wallet) {
  //       tonConnectUI.openModal()
  //       return
  //     }
  //     const { ok, data } = await donationApi(1, 'TON')

  //     if (!ok || !data) {
  //       throw new Error('Failed to create order')
  //     }

  //     const orderID = data.order.order_id
  //     const payload = beginCell()
  //       .storeUint(0, 32)
  //       .storeStringTail(orderID)
  //       .endCell()
  //       .toBoc()
  //       .toString('base64')

  //     const transaction = {
  //       validUntil: Math.floor(
  //         new Date(data.order.expires_at).getTime() / 1000
  //       ),
  //       messages: [
  //         {
  //           address: data.order.address,
  //           amount: String(data.order.amount),
  //           payload: payload,
  //         },
  //       ],
  //     }

  //     await tonConnectUI
  //       .sendTransaction(transaction)
  //       .then(() => {
  //         setOverlayQueue([{ type: 'transactionProcessing' }])
  //         pollOrderStatus(orderID, onPaid)
  //       })
  //       .catch(() => {})
  //   },
  //   [wallet, tonConnectUI, pollOrderStatus]
  // )

  return {
    purchaseForXTR,
    purchaseForTON,
    purchaseHintForXTR,
    donateForXTR,
    donateForTON,
    // payHintForTON,
  }
}
